import filters from '@/filters/index';

/**
 * Global mixin that exposes the organization's picker and display-calendar
 * preferences, plus a single date-formatting entry point.
 *
 * Stored dates remain Gregorian wall-clock values. The system `calendarType`
 * chooses native Gregorian inputs versus the Hebrew picker; the separate
 * `displayCalendarType` only changes formatted admin labels.
 *
 * Picker behavior follows the system `calendarType`. Date-label formatting
 * follows `displayCalendarType`, with legacy organizations falling back to
 * their system calendar setting.
 */
export default {
  computed: {
    displayCalendarType(): 'hebrew' | 'gregorian' {
      const self = this as any;
      // Prefer the freshly-fetched organization slice (never persisted, so never
      // stale). Fall back to the account's nested organization, which is
      // persisted to localStorage and only refreshed on login / app boot.
      const org =
        self.$store?.getters?.organization?.data ||
        self.$store?.getters?.account?.data?.organization ||
        null;
      const preference = org?.displayCalendarType || org?.calendarType || 'gregorian';
      return preference === 'hebrew' ? 'hebrew' : 'gregorian';
    },
    datePickerCalendarType(): 'hebrew' | 'gregorian' {
      const self = this as any;
      const org =
        self.$store?.getters?.organization?.data ||
        self.$store?.getters?.account?.data?.organization ||
        null;
      const preference = org?.calendarType || org?.displayCalendarType || 'gregorian';
      return preference === 'hebrew' ? 'hebrew' : 'gregorian';
    },
    // Existing date-input branches consume this flag. It intentionally follows
    // the system Calendar Type setting, not the separate label display setting.
    isGregorianDisplay(): boolean {
      return (this as any).datePickerCalendarType === 'gregorian';
    },
  },
  methods: {
    // Single date label used across the admin UI. Returns '-' for empty/invalid
    // input in both calendars, so existing `label === '-' ? '' : label` guards
    // keep working unchanged.
    formatConfiguredDate(value: unknown): string {
      if (value === null || value === undefined || value === '') return '-';
      return (this as any).displayCalendarType === 'gregorian'
        ? filters.convertToGregorianDate(value as string | Date)
        : filters.convertToHebrewDate(value as string);
    },
  },
};
